import React, { useEffect, useState } from "react";
import { Row, Col, Card, Button, Container, Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  BsBuildings,
  BsCashStack,
  BsPersonVcard,
  BsJournalCheck,
  BsEnvelope,
  BsTelephoneOutbound,
  BsArrowRightShort,
  BsStars,
} from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import WebService from "../../../Services/WebService";
import RmAiEmailModal from "../Modals/RmAiEmailModal";
import RmLogActivityModal from "../Modals/RmLogActivityModal";

const RmDashboard = () => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState(localStorage.getItem("name") || "Relation Manager");

  const [entrepreneurCount, setEntrepreneurCount] = useState(0);
  const [capitalBridgeCount, setCapitalBridgeCount] = useState<number | null>(null);
  const [globalCount, setGlobalCount] = useState<number | null>(null);
  const [comboCount, setComboCount] = useState<number | null>(null);

  const [investorCount, setInvestorCount] = useState(0);
  const [mentorCount, setMentorCount] = useState(0);

  const [mailLogsCount, setMailLogsCount] = useState(0);
  const [callLogsCount, setCallLogsCount] = useState(0);

  const [showAiEmailModal, setShowAiEmailModal] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);

  useEffect(() => {
    fetchCounts();
    loadLocalLogsCount();
    window.addEventListener("rm_logs_updated", loadLocalLogsCount);
    return () => window.removeEventListener("rm_logs_updated", loadLocalLogsCount);
  }, []);

  const fetchCounts = () => {
    WebService.getAPI({ action: "rm-user/counts", body: null })
      .then((res: any) => {
        const counts = res?.data || res?.info || res || {};
        setEntrepreneurCount(counts?.total_entrepreneurs ?? counts?.entrepreneurs ?? 0);
        setInvestorCount(counts?.total_investors ?? counts?.investors ?? 0);
        setMentorCount(counts?.total_mentors ?? counts?.mentors ?? 0);

        if (
          counts?.capital_bridge !== undefined
        ) {
          setCapitalBridgeCount(
            counts?.capital_bridge ?? 0
          );
          setGlobalCount(
            counts?.global_connect ?? 0
          );
          setComboCount(
            counts?.combo ?? 0
          );
        } else {
          fetchSubCounts();
        }
      })
      .catch((error: any) => {
        console.error(error);
        setEntrepreneurCount(0);
        setInvestorCount(0);
        setMentorCount(0);
        fetchSubCounts();
      });
  };

  const fetchSubCounts = () => {
    Promise.all([
      WebService.getAPI({ action: "rm-user/list/entrepreneur/investment?status=ALL&offset=0&limit=1", body: null }),
      WebService.getAPI({ action: "rm-user/list/entrepreneur/want-to-grow?status=ALL&offset=0&limit=1", body: null }),
      WebService.getAPI({ action: "rm-user/list/entrepreneur/combo?status=ALL&offset=0&limit=1", body: null }),
    ])
      .then(([cbRes, gRes, comboRes]: any[]) => {
        setCapitalBridgeCount(cbRes?.count ?? cbRes?.list?.length ?? 0);
        setGlobalCount(gRes?.count ?? gRes?.list?.length ?? 0);
        setComboCount(comboRes?.count ?? comboRes?.list?.length ?? 0);
      })
      .catch(() => { });
  };

  const loadLocalLogsCount = () => {
    const mails = JSON.parse(localStorage.getItem("rm_mail_logs") || "[]");
    const calls = JSON.parse(localStorage.getItem("rm_call_logs") || "[]");

    setMailLogsCount(mails.length);
    setCallLogsCount(calls.length);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  const renderEntrepreneurTooltip = (props: any) => (
    <Tooltip id="entrepreneur-tooltip" {...props}>
      <div className="p-2 text-start font-12" style={{ minWidth: "190px" }}>
        <div className="fw-bold mb-2 pb-1 border-bottom border-secondary text-warning font-13 d-flex align-items-center justify-content-between">
          <span>Entrepreneurs Breakdown</span>
          <BsBuildings />
        </div>
        <div className="d-flex align-items-center justify-content-between gap-3 py-1">
          <span>Capital Bridge:</span>
          <span className="fw-bold text-success font-13">{capitalBridgeCount ?? "-"}</span>
        </div>
        <div className="d-flex align-items-center justify-content-between gap-3 py-1">
          <span>Global:</span>
          <span className="fw-bold text-info font-13">{globalCount ?? "-"}</span>
        </div>
        <div className="d-flex align-items-center justify-content-between gap-3 py-1">
          <span>Combo:</span>
          <span className="fw-bold text-warning font-13">{comboCount ?? "-"}</span>
        </div>
      </div>
    </Tooltip>
  );

  return (
    <div className="page-content pb-5" style={{ minHeight: "calc(100vh - 70px)", background: "linear-gradient(135deg, #f8f9fa 0%, #f1f4f8 100%)" }}>
      <Container fluid className="px-4 pt-3">
        {/* Welcome Header */}
        <div className="d-flex flex-wrap align-items-center justify-content-between mb-4 bg-white p-4 rounded-4 shadow-sm border">
          <div>
            <span className="badge bg-primary-subtle text-primary font-12 px-3 py-1.5 rounded-pill mb-2 fw-semibold">
              Relation Manager Workspace
            </span>
            <h3 className="fw-bold text-dark mb-1">
              {getGreeting()}, <span className="text-primary">{userName}</span> 👋
            </h3>
            <p className="text-muted mb-0 font-14">
              Welcome to your RM Portal. Monitor user profiles, engage via AI Email Drafts, and track communication activities.
            </p>
          </div>
          <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
            <Button
              variant="outline-primary"
              className="rounded-pill px-3 py-2 font-13 fw-semibold d-flex align-items-center gap-1.5"
              onClick={() => setShowAiEmailModal(true)}
            >
              <BsStars className="text-warning" /> Compose AI Mail
            </Button>
            <Button
              variant="primary"
              className="rounded-pill px-3 py-2 font-13 fw-semibold d-flex align-items-center gap-1.5"
              onClick={() => setShowLogModal(true)}
            >
              <BsTelephoneOutbound /> Log Call
            </Button>
          </div>
        </div>

        {/* Stats Row */}
        <Row className="g-3 mb-4">
          <Col lg={3} sm={6}>
            <OverlayTrigger
              placement="top"
              delay={{ show: 150, hide: 200 }}
              overlay={renderEntrepreneurTooltip}
            >
              <Card
                className="border-0 shadow-sm rounded-4 h-100 overflow-hidden"
                onClick={() => navigate("/rm/entrepreneurs")}
                style={{ cursor: "pointer" }}
              >
                <Card.Body className="p-3.5 d-flex align-items-center">
                  <div className="p-3 rounded-4 bg-primary-subtle text-primary me-3">
                    <BsBuildings size={28} />
                  </div>
                  <div>
                    <small className="text-muted fw-semibold font-12 text-uppercase">Entrepreneurs</small>
                    <h3 className="fw-bold mb-0 text-dark">{entrepreneurCount}</h3>
                    <small className="text-success font-11">Capital Bridge & Global Connect</small>
                  </div>
                </Card.Body>
              </Card>
            </OverlayTrigger>
          </Col>

          <Col lg={3} sm={6}>
            <Card className="border-0 shadow-sm rounded-4 h-100 overflow-hidden" onClick={() => navigate("/rm/investors")} style={{ cursor: "pointer" }}>
              <Card.Body className="p-3.5 d-flex align-items-center">
                <div className="p-3 rounded-4 bg-success-subtle text-success me-3">
                  <BsCashStack size={28} />
                </div>
                <div>
                  <small className="text-muted fw-semibold font-12 text-uppercase">Investors</small>
                  <h3 className="fw-bold mb-0 text-dark">{investorCount}</h3>
                  <small className="text-success font-11">Active Investors</small>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={3} sm={6}>
            <Card className="border-0 shadow-sm rounded-4 h-100 overflow-hidden" onClick={() => navigate("/rm/mentors")} style={{ cursor: "pointer" }}>
              <Card.Body className="p-3.5 d-flex align-items-center">
                <div className="p-3 rounded-4 bg-warning-subtle text-warning me-3">
                  <BsPersonVcard size={28} />
                </div>
                <div>
                  <small className="text-muted fw-semibold font-12 text-uppercase">Mentors</small>
                  <h3 className="fw-bold mb-0 text-dark">{mentorCount}</h3>
                  <small className="text-warning font-11">Expert Mentors</small>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={3} sm={6}>
            <Card className="border-0 shadow-sm rounded-4 h-100 overflow-hidden" onClick={() => navigate("/rm/interaction-logs")} style={{ cursor: "pointer" }}>
              <Card.Body className="p-3.5 d-flex align-items-center">
                <div className="p-3 rounded-4 bg-info-subtle text-info me-3">
                  <BsJournalCheck size={28} />
                </div>
                <div>
                  <small className="text-muted fw-semibold font-12 text-uppercase">Total Activities</small>
                  <h3 className="fw-bold mb-0 text-dark">{mailLogsCount + callLogsCount}</h3>
                  <small className="text-info font-11">Mails & Calls</small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* View Mode Portals Section */}
        <h5 className="fw-bold text-dark mb-3">User Directories & View Mode</h5>
        <Row className="g-4 mb-5">
          <Col md={4}>
            <Card className="border-0 shadow-sm rounded-4 h-100 hover-shadow transition-all">
              <Card.Body className="p-4 d-flex flex-column">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="p-3 rounded-3 bg-primary text-white">
                    <BsBuildings size={24} />
                  </div>
                  <Badge bg="primary" pill>Capital Bridge / Global / Combo</Badge>
                </div>
                <h4 className="fw-bold text-dark mb-2">Entrepreneurs Directory</h4>
                <p className="text-muted font-14 mb-4 flex-grow-1">
                  Access complete view mode for all entrepreneurs seeking capital raising, startup growth, or acquisition opportunities.
                </p>
                <Button variant="outline-primary" className="rounded-pill w-100 fw-semibold d-flex align-items-center justify-content-center gap-1" onClick={() => navigate("/rm/entrepreneurs")}>
                  View Entrepreneurs <BsArrowRightShort size={20} />
                </Button>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            <Card className="border-0 shadow-sm rounded-4 h-100 hover-shadow transition-all">
              <Card.Body className="p-4 d-flex flex-column">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="p-3 rounded-3 bg-success text-white">
                    <BsCashStack size={24} />
                  </div>
                  <Badge bg="success" pill>Investors</Badge>
                </div>
                <h4 className="fw-bold text-dark mb-2">Investors Directory</h4>
                <p className="text-muted font-14 mb-4 flex-grow-1">
                  View individual and institutional investors, investment preferences, contact details, and engagement status.
                </p>
                <Button variant="outline-success" className="rounded-pill w-100 fw-semibold d-flex align-items-center justify-content-center gap-1" onClick={() => navigate("/rm/investors")}>
                  View Investors <BsArrowRightShort size={20} />
                </Button>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            <Card className="border-0 shadow-sm rounded-4 h-100 hover-shadow transition-all">
              <Card.Body className="p-4 d-flex flex-column">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="p-3 rounded-3 bg-warning text-white">
                    <BsPersonVcard size={24} />
                  </div>
                  <Badge bg="warning" text="dark" pill>Mentors</Badge>
                </div>
                <h4 className="fw-bold text-dark mb-2">Mentors Directory</h4>
                <p className="text-muted font-14 mb-4 flex-grow-1">
                  Explore onboarded industry mentors, domain expertise, consultation areas, and engagement history.
                </p>
                <Button variant="outline-warning" className="rounded-pill w-100 fw-semibold d-flex align-items-center justify-content-center gap-1 text-dark" onClick={() => navigate("/rm/mentors")}>
                  View Mentors <BsArrowRightShort size={20} />
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Activity & Logs Summary Section */}
        <h5 className="fw-bold text-dark mb-3">Communication & Activity Logs Overview</h5>
        <Row className="g-3">
          <Col md={6}>
            <Card className="border-0 shadow-sm rounded-4 p-3 bg-white">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="fw-bold text-dark font-15 d-flex align-items-center gap-2">
                  <BsEnvelope className="text-primary" /> Mails Logged
                </span>
                <Badge bg="primary" pill>{mailLogsCount}</Badge>
              </div>
              <p className="font-13 text-muted mb-3">Emails drafted using AI & sent directly to platform users.</p>
              <Button variant="light" size="sm" className="w-100 rounded-pill font-12 fw-semibold text-primary" onClick={() => navigate("/rm/interaction-logs")}>
                View Mail Logs
              </Button>
            </Card>
          </Col>

          <Col md={6}>
            <Card className="border-0 shadow-sm rounded-4 p-3 bg-white">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="fw-bold text-dark font-15 d-flex align-items-center gap-2">
                  <BsTelephoneOutbound className="text-success" /> Calls Done Logs
                </span>
                <Badge bg="success" pill>{callLogsCount}</Badge>
              </div>
              <p className="font-13 text-muted mb-3">Track logged phone calls, discussion outcomes, and follow-ups.</p>
              <Button variant="light" size="sm" className="w-100 rounded-pill font-12 fw-semibold text-success" onClick={() => navigate("/rm/interaction-logs")}>
                View Call Logs
              </Button>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Modals */}
      <RmAiEmailModal
        show={showAiEmailModal}
        onHide={() => setShowAiEmailModal(false)}
      />

      <RmLogActivityModal
        show={showLogModal}
        onHide={() => setShowLogModal(false)}
      />
    </div>
  );
};

export default RmDashboard;
